import { request, requestData } from '@/lib/api-client';
import type { RiderCashBalance, RiderProfile } from './types';

export const riderApi = {
  /**
   * The caller's own profile.
   *
   * Scoped to the signed-in user by construction — there is no id in the path,
   * so there is nothing to get wrong and no way to read someone else's. It
   * answers 404 for a signed-in user the platform has not onboarded as a
   * rider, which is a state this app has to render rather than treat as a
   * failure. See `useRiderProfile`.
   */
  me(): Promise<RiderProfile> {
    return requestData<RiderProfile>('/me/rider');
  },

  /**
   * Going on or off shift.
   *
   * `BUSY` and `SUSPENDED` are never sent from here: the backend sets BUSY
   * itself when a job is accepted, and refuses a rider who tries to suspend —
   * or un-suspend — themselves.
   */
  setOnShift(riderId: string, onShift: boolean): Promise<RiderProfile> {
    return requestData<RiderProfile>(`/riders/${riderId}/status`, {
      method: 'PATCH',
      body: { status: onShift ? 'AVAILABLE' : 'OFFLINE' },
    });
  },

  /**
   * Cash the platform believes is in this rider's pocket.
   *
   * Resolved from the token like the profile is. Every COD handover writes a
   * custody entry server-side whether or not anything displays it, so the
   * alternative to this screen is not "no ledger" — it is a ledger the person
   * carrying the money cannot see.
   */
  cashBalance(): Promise<RiderCashBalance> {
    return requestData<RiderCashBalance>('/me/rider/cash-balance');
  },

  /**
   * A GPS ping. 204 and no body — there is nothing useful to return, and a
   * body would only cost bandwidth on a moving connection.
   *
   * Given a short timeout of its own: this fires every twenty seconds all
   * shift, and one that has not landed by the time the next is due has been
   * overtaken by a position the rider has already left.
   */
  pushLocation(
    riderId: string,
    position: { latitude: number; longitude: number; accuracyM?: number },
  ): Promise<void> {
    return request<void>(`/riders/${riderId}/locations`, {
      method: 'POST',
      body: position,
      timeoutMs: 10_000,
    });
  },
};
